/**
 * PDP Shipping Estimate — Hoellein
 *
 * Shows what checkout would offer for ONE unit of the product. Nothing is hardcoded here: zones, rates and
 * prices come from the shop metafield hoellein.shipping_rates, which scripts/shopify/sync-shipping.mjs
 * generates from Shopify's delivery profile (Settings → Shipping & Delivery). Rules mirror ShipFit
 * (class winner/override, companion rules, class token in the rate name, safety net) plus Shopify's price and
 * weight conditions. Rule config lives in data/shipping/hoellein.yaml.
 */
const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const esc = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const needle = (text) => new RegExp(`${esc(text)}(?!\\d)`, 'i');
const html = (text) => String(text).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

/** "[\"960 - Spedition XXL (nur D)\"]" | "960 - …" | 960 → "960" */
export function extractCode(raw) {
  if (raw == null || raw === '') return null;
  let value = raw;
  try {
    const parsed = JSON.parse(raw);
    value = Array.isArray(parsed) ? String(parsed[0] ?? '') : String(parsed);
  } catch {}
  return String(value).match(/^\s*(\d+)/)?.[1] ?? null;
}

function visibleCodes(winner, config, subtotal) {
  if (winner.override) return [winner.code];
  const companion = (config.companion_rules || []).find((r) => r.whenWinner === winner.code);
  if (!companion) return [winner.code];
  const threshold = companion.hideWinnerAboveSubtotal;
  if (threshold !== undefined && subtotal >= threshold) return companion.alsoShow;
  return [winner.code, ...companion.alsoShow];
}

export function maskName(name, config) {
  const classToken = new RegExp(esc(config.rate_token).replace(esc('{code}'), '\\d+'), 'gi');
  let out = name.replace(classToken, '');
  out = out.replace(/\(\s*\)|\[\s*\]/g, '').replace(/\s+/g, ' ').trim();
  return out.replace(/^[\s\-–—|/,:;]+|[\s\-–—|/,:;]+$/g, '');
}

function inBand(rate, price, kg) {
  if (rate.min_price != null && price < rate.min_price) return false;
  if (rate.max_price != null && price > rate.max_price) return false;
  if (rate.min_kg != null && kg < rate.min_kg) return false;
  if (rate.max_kg != null && kg > rate.max_kg) return false;
  return true;
}

/** Rates the checkout would offer for one unit (class code, price in EUR, weight in kg) to `country`. */
export function ratesFor(config, { country, classCode, price, kg }) {
  const zones = config.zones.filter((z) => z.countries.includes(country));
  if (!zones.length) return { zones, winner: null, shipping: [], pickup: [], better: null };

  const winner = config.classes.find((c) => c.code === classCode) ?? null;
  const codes = winner ? visibleCodes(winner, config, price) : null;
  const matchers = codes ? codes.map((code) => needle(config.rate_token.split('{code}').join(code))) : null;
  const inClass = (r) => matchers === null || matchers.some((m) => m.test(r.name));

  const all = zones.flatMap((z) => z.rates);
  const banded = all.filter((r) => r.pickup || inBand(r, price, kg));
  const shippingAll = banded.filter((r) => !r.pickup);
  const openZone = config.safety_net === 'show-all' && !winner?.override && !shippingAll.some(inClass);
  // the same visible rate (e.g. EuroPaket for class 400 and 500) is listed once
  const seen = new Set();
  const shipping = shippingAll.filter((r) => (openZone || inClass(r)) && !seen.has(maskName(r.name, config) + r.price) && seen.add(maskName(r.name, config) + r.price))
    .sort((a, b) => a.price - b.price);

  // hint: a cheaper rate of this class that only needs a higher order value (e.g. free from 129 EUR)
  const cheapest = Math.min(...shipping.map((r) => r.price));
  const better = all
    .filter((r) => !r.pickup && r.min_price > price && r.price < cheapest && inClass(r)
      && (r.min_kg == null || kg >= r.min_kg) && (r.max_kg == null || kg <= r.max_kg))
    .sort((a, b) => a.min_price - b.min_price)[0] ?? null;

  const pickup = banded.filter((r) => r.pickup).filter((r, i, a) => a.findIndex((x) => x.name === r.name) === i);
  return { zones, winner, shipping, pickup, better };
}

if (typeof HTMLElement !== 'undefined' && !customElements.get('m-pdp-shipping')) {
  class MPdpShipping extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.pdp-shipping-popup__panel');
      this.closeBtn = this.querySelector('.pdp-shipping-popup__close');
      this.countrySelect = this.querySelector('.pdp-shipping-popup__country-select');
      this.resultsEl = this.querySelector('.pdp-shipping-popup__results');
      try {
        this.config = JSON.parse(this.querySelector('[data-rates]')?.textContent || 'null');
      } catch {
        this.config = null;
      }
      if (!this.config?.zones) return;

      this.classCode = extractCode(this.dataset.versandklasse);
      this.price = Number(this.dataset.price || 0);
      this.kg = Number(this.dataset.weightKg || 0);
      this.buildCountries();

      this.triggerBtn = this.parentElement?.querySelector('[data-open-pdp-shipping]');
      this.triggerBtn?.addEventListener('click', () => this.open());
      this.closeBtn.addEventListener('click', () => this.close());
      this.countrySelect.addEventListener('change', () => this.lookup());
      document.addEventListener('click', (e) => {
        if (!this.panel.hidden && !this.contains(e.target) && !this.triggerBtn?.contains(e.target)) this.close();
      });
      this.lookup();
    }

    open() { this.panel.hidden = false; }
    close() { this.panel.hidden = true; }

    buildCountries() {
      const names = new Intl.DisplayNames([document.documentElement.lang || 'de'], { type: 'region' });
      const codes = [...new Set(this.config.zones.flatMap((z) => z.countries))].filter((c) => c !== 'REST');
      codes.sort((a, b) => (a === 'DE') - (b === 'DE') || names.of(a).localeCompare(names.of(b)));
      if (codes.includes('DE')) codes.unshift(codes.pop());
      const preferred = codes.includes(this.dataset.defaultCountry) ? this.dataset.defaultCountry : codes[0];
      const placeholder = this.countrySelect.querySelector('option[value=""]')?.outerHTML ?? '';
      this.countrySelect.innerHTML = placeholder + codes
        .map((c) => `<option value="${c}"${c === preferred ? ' selected' : ''}>${html(names.of(c))}</option>`).join('');
    }

    _t(key, vars) {
      let str = (window.pdpShippingI18n || {})[key] || key;
      for (const [k, v] of Object.entries(vars || {})) str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
      return str;
    }

    row(rate, zones) {
      const taxNote = !rate.pickup && zones.some((z) => z.rates.includes(rate) && (this.config.tax_note_zone_prefixes || []).some((p) => z.name.startsWith(p)))
        ? ` <em class="pdp-shipping-popup__tax-note">${this._t('tax_note')}</em>` : '';
      const price = rate.price === 0
        ? `<span class="pdp-shipping-popup__rate-free">${this._t('free')}</span>`
        : `<span>${euro.format(rate.price)}</span>`;
      const details = rate.details ? ` <em class="pdp-shipping-popup__threshold-hint">${html(rate.details)}</em>` : '';
      return `<p class="pdp-shipping-popup__rate-item"><span class="pdp-shipping-popup__rate-name">${html(maskName(rate.name, this.config))}</span>${taxNote}: ${price}${details}</p>`;
    }

    lookup() {
      const country = this.countrySelect.value;
      this.resultsEl.innerHTML = '';
      if (!country) return;

      const { zones, winner, shipping, pickup, better } = ratesFor(this.config, {
        country, classCode: this.classCode, price: this.price, kg: this.kg,
      });

      if (winner?.override && !pickup.length) {
        this.resultsEl.innerHTML = `<p class="pdp-shipping-popup__no-ship">${this._t('pickup_only')}</p>`;
        return;
      }
      if (this.classCode && !winner) {
        this.resultsEl.innerHTML = `<p class="pdp-shipping-popup__no-ship">${this._t('unknown_class_html', { class: this.classCode })}</p>`;
        return;
      }

      let out = '';
      if (shipping.length) {
        out += shipping.map((r) => this.row(r, zones)).join('');
        if (better) {
          const hint = better.price === 0
            ? this._t('free_hint_html', { amount: euro.format(better.min_price) })
            : `${euro.format(better.price)} ${this._t('from_amount', { amount: euro.format(better.min_price) })}`;
          out += `<p class="pdp-shipping-popup__rate-item"><em class="pdp-shipping-popup__threshold-hint">${hint}</em></p>`;
        }
        if (zones.length > 1) out += `<p class="pdp-shipping-popup__rate-item"><em class="pdp-shipping-popup__threshold-hint">${this._t('region_dependent')}</em></p>`;
      } else if (winner?.override) {
        out += `<p class="pdp-shipping-popup__no-ship">${this._t('pickup_only')}</p>`;
      } else {
        out += `<p class="pdp-shipping-popup__no-ship">${this._t('no_ship_to_country')}</p>`;
      }
      out += pickup.map((r) => this.row(r, zones)).join('');
      this.resultsEl.innerHTML = out;
    }
  }
  customElements.define('m-pdp-shipping', MPdpShipping);
}
